<script setup lang="ts">
import { RouterView, useRoute } from 'vue-router'
import { ref, watch } from 'vue'
import { useThemeStore } from './stores/theme'

const themeStore = useThemeStore()

const route = useRoute()
const mobileMenuOpen = ref(false)

const navLinks = [
  { path: '/', label: 'Dashboard', icon: 'home' },
  { path: '/sales', label: 'Vendas', icon: 'chart' },
  { path: '/inventory', label: 'Produtos', icon: 'box' },
  { path: '/clients', label: 'Clientes', icon: 'users' },
]

function isActive(path: string) {
  if (path === '/') return route.path === '/'
  return route.path.startsWith(path)
}

// Close mobile menu on navigation
watch(() => route.path, () => {
  mobileMenuOpen.value = false
})

function getNavClass() {
  return 'border-surface-200 bg-white/80 dark:border-surface-800 dark:bg-surface-900/80'
}

function getNavLinkClass(isActive: boolean) {
  return isActive
    ? 'bg-brand-50 text-brand-700 dark:bg-brand-900/30 dark:text-brand-300'
    : 'text-surface-500 hover:bg-surface-100 hover:text-surface-700 dark:text-surface-400 dark:hover:bg-surface-800 dark:hover:text-surface-200'
}

function getMobileNavClass(isActive: boolean) {
  return getNavLinkClass(isActive)
}

function getMobileNavContainerClass() {
  return 'bg-white/95 dark:border-surface-800 dark:bg-surface-900/95'
}
</script>

<template>
  <div class="min-h-screen bg-surface-50 dark:bg-surface-950">
    <nav :class="getNavClass()" class="sticky top-0 z-40 backdrop-blur-lg">
      <div class="mx-auto flex h-14 max-w-7xl items-center justify-between px-3 sm:h-16 sm:px-6 lg:px-8">
        <div class="flex items-center gap-2 sm:gap-8">
          <router-link to="/" class="flex items-center gap-2">
            <div class="flex h-8 w-8 items-center justify-center rounded-xl bg-brand-600">
              <svg class="h-4 w-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                <path stroke-linecap="round" stroke-linejoin="round" d="M2.036 12.25a8.681 8.681 0 01-.159-.75C1.688 10.057 3.256 8 5.25 8c.82 0 1.607.22 2.285.58m0 0c0 .94.168 1.842.477 2.68m-.477-2.68a8.69 8.69 0 001.545-.786m-2.022 3.466c-.81.649-1.827 1.264-3.026 1.264-1.425 0-2.666-.782-3.486-1.966m2.022-3.466C6.55 8.201 7.75 7.5 9.25 7.5c1.023 0 1.966.263 2.79.728M18.75 10.5c-1.199 0-2.217-.615-3.027-1.264m3.027 1.264c.31.837.477 1.74.477 2.68M18.75 10.5c1.024 0 1.966.264 2.79.728m0 0A8.681 8.681 0 0121.964 12.25c-.159.5-.348.976-.56 1.428M21.14 11.228a8.75 8.75 0 01-.56 1.428m0 0a8.696 8.696 0 01-1.77 2.407 8.697 8.697 0 01-4.31 1.793m-5.26-4.2a8.696 8.696 0 00-1.77 2.408 8.697 8.697 0 004.31 1.793m5.26-4.2c.308.838.477 1.74.477 2.68m0 0a8.75 8.75 0 01-1.77 2.407 8.697 8.697 0 01-4.31 1.793" />
              </svg>
            </div>
            <span class="text-surface-800 dark:text-surface-100">Ótica</span>
          </router-link>

          <!-- Theme toggle -->
          <button
            @click="themeStore.toggleTheme"
            class="relative rounded-xl p-2 transition-colors hover:bg-surface-100 dark:hover:bg-surface-800"
            aria-label="Alternar tema"
          >
            <svg v-if="!themeStore.isDark" class="h-5 w-5 text-surface-600 dark:text-surface-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
              <path stroke-linecap="round" stroke-linejoin="round" d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
            </svg>
            <svg v-else class="h-5 w-5 text-surface-300 dark:text-yellow-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
              <path stroke-linecap="round" stroke-linejoin="round" d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
            </svg>
          </button>

           <!-- Desktop nav -->
           <div class="hidden sm:flex items-center gap-1">
             <router-link
               v-for="link in navLinks"
               :key="link.path"
               :to="link.path"
               class="flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-all duration-150"
               :class="getNavLinkClass(isActive(link.path))"
             >
              <svg
                v-if="link.icon === 'home'"
                class="h-4 w-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                stroke-width="2"
              >
                <path stroke-linecap="round" stroke-linejoin="round" d="M2.25 12l8.954-8.955a1.126 1.126 0 011.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75M8.25 21h8.25" />
              </svg>
              <svg
                v-else-if="link.icon === 'chart'"
                class="h-4 w-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                stroke-width="2"
              >
                <path stroke-linecap="round" stroke-linejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z" />
              </svg>
              <svg
                v-else-if="link.icon === 'box'"
                class="h-4 w-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                stroke-width="2"
              >
                <path stroke-linecap="round" stroke-linejoin="round" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
              </svg>
              <svg
                v-else
                class="h-4 w-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                stroke-width="2"
              >
                <path stroke-linecap="round" stroke-linejoin="round" d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z" />
              </svg>
              {{ link.label }}
            </router-link>
          </div>
        </div>

        <!-- Mobile hamburger button -->
        <button
          :class="getMobileNavClass(false)"
          class="sm:hidden flex h-10 w-10 items-center justify-center rounded-lg transition-colors"
          @click="mobileMenuOpen = !mobileMenuOpen"
          aria-label="Menu"
        >
          <svg v-if="!mobileMenuOpen" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />
          </svg>
          <svg v-else class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

       <!-- Mobile nav dropdown -->
       <div
         v-if="mobileMenuOpen"
         :class="getMobileNavContainerClass()"
         class="sm:hidden border-t backdrop-blur-lg animate-slide-down"
       >
        <div class="space-y-1 px-3 py-3">
           <router-link
             v-for="link in navLinks"
             :key="link.path"
             :to="link.path"
             class="flex items-center gap-3 rounded-lg px-3 py-3 text-sm font-medium transition-all duration-150"
             :class="getMobileNavClass(isActive(link.path))"
           >
            <svg
              v-if="link.icon === 'home'"
              class="h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              stroke-width="2"
            >
              <path stroke-linecap="round" stroke-linejoin="round" d="M2.25 12l8.954-8.955a1.126 1.126 0 011.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75M8.25 21h8.25" />
            </svg>
            <svg
              v-else-if="link.icon === 'chart'"
              class="h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              stroke-width="2"
            >
              <path stroke-linecap="round" stroke-linejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z" />
            </svg>
            <svg
              v-else-if="link.icon === 'box'"
              class="h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              stroke-width="2"
            >
              <path stroke-linecap="round" stroke-linejoin="round" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
            </svg>
            <svg
              v-else
              class="h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              stroke-width="2"
            >
              <path stroke-linecap="round" stroke-linejoin="round" d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z" />
            </svg>
            {{ link.label }}
          </router-link>
        </div>
      </div>
    </nav>
    <main>
      <RouterView />
    </main>
  </div>
</template>

